import { Component, Inject, Input, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NgRedux } from '@angular-redux/store';
import { PageScrollInstance, PageScrollService } from 'ngx-page-scroll';
import { IAppConfig } from '../../store';
import { APP_CONFIG } from '../../api/config';
import {
  Reservation, ReservationChangeType, MessageCategory, ReservationAvailabilityError,
  ReservationErrorMessageKeys
} from '../../api/model';
import { AppAction, IAppState } from '../../store/interfaces/IAppState';
import { ActionHandler } from '../../store/action.handler';
import { IMessage } from '../../api/interfaces/Messages/IMessage';
import { ModifyReservationDelegate } from '../../api/delegates/modify-reservation.delegate';
import { useDefault } from '../../common/common';
import { MessagesUtil } from '../../common/messages.util';

const STAY_DETAILS_CHANGE_TYPES: Array<ReservationChangeType> = Array.of(ReservationChangeType.CHECK_IN_DATE,
  ReservationChangeType.CHECK_OUT_DATE, ReservationChangeType.NUMBER_OF_ADULTS,
  ReservationChangeType.NUMBER_OF_CHILDREN, ReservationChangeType.NUMBER_OF_ROOMS);
const TOP_ID = '#changeStayDetailsTop';
@Component({
  selector: 'modify-stay-details',
  templateUrl: './modify-stay-details.component.html',
  styleUrls: ['./modify-stay-details.component.scss']
})
export class ModifyStayDetailsComponent implements OnInit {
  AppAction = AppAction;
  @Input() isOpen: boolean;
  @Input() messages;
  @Input() isRateSelection: boolean;
  messageCategory: MessageCategory;
  state: IAppState;
  reservation: Reservation;
  reservationChanges: Map<ReservationChangeType, any>;
  availabilityError: any = null;
  message: IMessage = null;
  hasStayDetailsChanges: boolean;
  hasChangesToClear: boolean;
  private _appConfig: IAppConfig;
  selectedCheckInDate: Date;
  selectedCheckOutDate: Date;
  selectedNumberOfAdults: number;
  selectedNumberOfChildren: number;
  selectedNumberOfRooms: number;

  constructor(
    @Inject(APP_CONFIG) appConfig,
    private _ngRedux: NgRedux<IAppState>,
    private _modifyReservationDelegate: ModifyReservationDelegate,
    private pageScrollService: PageScrollService,
    @Inject(DOCUMENT) private document) {
    this._appConfig = appConfig;
  }
  ngOnInit() {
    this.hasStayDetailsChanges = false;
    this.hasChangesToClear = false;
    this.updateFromState();
    this._ngRedux.subscribe(() => {
      this.updateFromState();
    });
    this.messageCategory = MessageCategory.ERROR_CHECK_AVAILABILITY;
  }

  updateFromState() {
    this.state = this._ngRedux.getState();
    this.reservation = useDefault(this.state.reservation, new Reservation());
    this.reservationChanges = this.state.reservationChanges;
    this.selectedCheckInDate = useDefault(this.reservationChanges.get(ReservationChangeType.CHECK_IN_DATE), this.reservation.stay.checkInDate);
    this.selectedCheckOutDate = useDefault(this.reservationChanges.get(ReservationChangeType.CHECK_OUT_DATE), this.reservation.stay.checkOutDate);
    this.selectedNumberOfAdults = useDefault(this.reservationChanges.get(ReservationChangeType.NUMBER_OF_ADULTS), this.reservation.stay.numberOfAdults);
    this.selectedNumberOfChildren = useDefault(this.reservationChanges.get(ReservationChangeType.NUMBER_OF_CHILDREN), this.reservation.stay.numberOfChildren);
    this.selectedNumberOfRooms = useDefault(this.reservationChanges.get(ReservationChangeType.NUMBER_OF_ROOMS), this.reservation.stay.numberOfRooms);
    ActionHandler.handleError(this.state, AppAction.CHECK_AVAILABILITY, () => {
      if (!this.hasStayDetailsChanges) {
        // We do not display component errors when there was an interaction with find hotels
        return;
      }
      if (this.state.error instanceof ReservationAvailabilityError && !this.state.error.invalidRooms) {
        this.availabilityError = this.state.error;
        return;
      }
      this.message = MessagesUtil.fromError(this._appConfig, this.state.error, ReservationErrorMessageKeys.AVAILABILITY_ERROR_UNKNOWN_KEY);
      this.goToTop();
    });
    const availableReservation = this.state.availableReservation;
    ActionHandler.handleSuccess(this.state, AppAction.CHECK_AVAILABILITY, () => {
      if (this.hasStayDetailsChanges && !!availableReservation) {
        this.message = MessagesUtil.toSuccessMessage(ReservationErrorMessageKeys.SUCCESS_STAY_AVAILABILITY);
        this.goToTop();
      }
      this.hasStayDetailsChanges = false;
      this.updateHasChangesToClear();
    });
    ActionHandler.handleSuccess(this.state, AppAction.UPDATE_CHANGES, () => {
      this.updateHasChangesToClear();
    });
  }

  selectCheckInDate(date: Date) {
    this.addChange(ReservationChangeType.CHECK_IN_DATE, date);
  }
  selectCheckOutDate(date: Date) {
    this.addChange(ReservationChangeType.CHECK_OUT_DATE, date);
  }
  selectNumberOfAdults(numberOf: number) {
    this.addChange(ReservationChangeType.NUMBER_OF_ADULTS, numberOf);
  }

  selectNumberOfChildren(numberOf: number) {
    this.addChange(ReservationChangeType.NUMBER_OF_CHILDREN, numberOf);
  }

  selectNumberOfRooms(numberOf: number) {
    this.addChange(ReservationChangeType.NUMBER_OF_ROOMS, numberOf);
  }

  addChange(changeType: ReservationChangeType, value) {
    this._modifyReservationDelegate.addChange(changeType, value);
    this.hasStayDetailsChanges = true;
  }
  checkAvailability() {
    this.message = null;
    this.availabilityError = null;
    this._modifyReservationDelegate.checkReservationAvailability();
  }
  clearChanges() {
    this.message = null;
    this.availabilityError = null;
    this.hasStayDetailsChanges = false;
    this._modifyReservationDelegate.clearReservationChanges(STAY_DETAILS_CHANGE_TYPES);
  }
  goToTop(): void {
    const pageScrollInstance: PageScrollInstance = PageScrollInstance.simpleInstance(this.document, TOP_ID);
    this.pageScrollService.start(pageScrollInstance);
  };
  updateHasChangesToClear(): void {
    this.hasChangesToClear = !!(Array.from(this.reservationChanges.keys()).find(element => !!STAY_DETAILS_CHANGE_TYPES.find(type => type === element)));
  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/components/modify-stay-details/modify-stay-details.component.ts